/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import PropTypes from 'prop-types';
import defaultsDeep from 'lodash/defaultsDeep';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import Tabs from '../../ui/Tabs';
import { Container, Row, Column, MediaQueries } from '../../../styles/Grid';
import { hideElementCss } from '../../../utils/responsive';
import Spacer from '../../ui/Spacer';
import DotComponent from '../../ui/DotComponent';
import { composeStringToId } from '../../../utils/stringToId';
import CtaLink from '../../ui/CtaLink';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import {
  FirstLgTabImage,
  SecondLgTabImage,
  ThirdLgTabImage,
  FourthLgTabImage,
} from './LgImages';
import {
  FirstTabImage,
  SecondTabImage,
  ThirdTabImage,
  FourthTabImage,
} from './SmImages';
import {
  spacerCss,
  containerPaddingCss,
  containerMarginCss,
  itemTabCss,
  textHeaderCss,
  contentTabItemCss,
  contentHeadlineCss,
  contentDescriptionCss,
  tabsContentCss,
  ctaLinkCss,
  tabsHeaderCss,
  TabCtaCss,
} from './index.styles';

export const defaultData = {
  BackgroundColor: {
    fields: {
      Value: {
        value: 'white',
      },
    },
  },
  items: [],
};

const imagesForSmallAndMedium = {
  image1: (imageRight, imageLeft) => FirstTabImage(imageRight, imageLeft),
  image2: (imageRight, imageLeft) => SecondTabImage(imageRight, imageLeft),
  image3: (imageRight, imageLeft) => ThirdTabImage(imageRight, imageLeft),
  image4: (imageRight, imageLeft) => FourthTabImage(imageRight, imageLeft),
};

const imageForLgAndUp = {
  image1: (imageRight, imageLeft, nextImage) =>
    FirstLgTabImage(imageRight, imageLeft, nextImage),
  image2: (imageRight, imageLeft, beforeImage, nextImage) =>
    SecondLgTabImage(imageRight, imageLeft, beforeImage, nextImage),
  image3: (imageRight, imageLeft, nextImage) =>
    ThirdLgTabImage(imageRight, imageLeft, nextImage),
  image4: (imageRight, imageLeft) => FourthLgTabImage(imageRight, imageLeft),
};

class TabHeader extends SafePureComponent {
  safeRender() {
    const { name, label, onChangeTabs, active } = this.props;
    return (
      <li css={itemTabCss} key={composeStringToId(label, 'regus-tabs')}>
        <button onClick={onChangeTabs} id={label} css={TabCtaCss}>
          <span data-active={active} css={textHeaderCss}>
            <Text field={name} />
          </span>
        </button>
      </li>
    );
  }
}

TabHeader.displayName = 'TabHeader';

class TabContent extends SafePureComponent {
  safeRender() {
    const { content, active } = this.props;
    const CTA = get(content, 'value.CTA', {});
    const isEmptyCta = isEmpty(CTA);
    return (
      <div css={contentTabItemCss} data-active={active}>
        <h2 css={contentHeadlineCss}>
          <Text field={content.value.Headline} />
          <DotComponent />
        </h2>
        <Row>
          <Column cols={{ sm: 6, md: [5, 6], lg: [5, 6], xl: [4, 5] }}>
            <p css={contentDescriptionCss}>
              <Text field={content.value.Description} />
            </p>
          </Column>
        </Row>
        {!isEmptyCta && (
          <CtaLink linkCustomCss={ctaLinkCss} cta={CTA} type="primary" />
        )}
      </div>
    );
  }
}

TabContent.displayName = 'TabContent';
const sendingInformationForImage = (arr, index) => {
  switch (index) {
    case 0:
    case 2:
      return imageForLgAndUp[`image${index + 1}`](
        get(arr[index], 'fields.content.value.imageRight', ''),
        get(arr[index], 'fields.content.value.imageLeft', ''),
        get(arr[index + 1], 'fields.content.value.imageLeft', ''),
      );
    case 1:
      return imageForLgAndUp[`image${index + 1}`](
        get(arr[index], 'fields.content.value.imageRight', ''),
        get(arr[index], 'fields.content.value.imageLeft', ''),
        get(arr[index - 1], 'fields.content.value.imageRight', ''),
        get(arr[index + 1], 'fields.content.value.imageLeft', ''),
      );
    case 3:
      return imageForLgAndUp[`image${index + 1}`](
        get(arr[index], 'fields.content.value.imageRight', ''),
        get(arr[index], 'fields.content.value.imageLeft', ''),
      );
  }
};

export class RegusTabs extends SafePureComponent {
  state = {
    selectedRegusLabel: '',
  };

  componentDidMount() {
    const fields = defaultsDeep(this.props.fields, defaultData);
    this.setState({
      selectedRegusLabel: get(fields, 'items[0].fields.label.value', ''),
    });
  }

  onChangeRegusTab = event => {
    this.setState({ selectedRegusLabel: event.target.id });
  };

  safeRender() {
    const fields = defaultsDeep(this.props.fields, defaultData);
    const firstItemActive = get(fields, 'items[0].fields.label.value', '');
    const { selectedRegusLabel } = this.state;
    return (
      <Spacer
        backgroundColor={fields.BackgroundColor}
        paddingCss={containerPaddingCss}
        marginCss={containerMarginCss}
        customCss={spacerCss}
      >
        <Container>
          <Row
            css={[
              hideElementCss([MediaQueries.sm, MediaQueries.md]),
              { position: 'relative' },
            ]}
          >
            {fields.items.map((item, index, arr) => {
              if (item.fields.label.value !== selectedRegusLabel) return null;
              return sendingInformationForImage(arr, index);
            })}
          </Row>
          <Row>
            <Column
              cols={{ sm: 6, md: 6, lg: 6, xl: 5 }}
              offsets={{ md: 1, lg: 3 }}
            >
              <Tabs
                showLeftEffect
                classNameTabsHeader={tabsHeaderCss}
                classNameTabsContent={tabsContentCss}
                firstItemActive={firstItemActive}
                renderTabs={(activeTab, onChangeTabs) =>
                  fields.items.map(item => {
                    const labelValue = get(item, 'fields.label.value', '');
                    return (
                      <TabHeader
                        active={labelValue === activeTab}
                        onChangeTabs={onChangeTabs}
                        key={composeStringToId(labelValue, 'regus-tab-header')}
                        name={item.fields.tabName}
                        label={labelValue}
                      />
                    );
                  })
                }
                renderContent={activeTab =>
                  fields.items.map(item => {
                    const labelValue = get(item, 'fields.label.value', '');
                    return (
                      <TabContent
                        active={labelValue === activeTab}
                        key={composeStringToId(labelValue, 'regus-tab-content')}
                        content={item.fields.content}
                      />
                    );
                  })
                }
                onChangeProp={this.onChangeRegusTab}
              />
            </Column>
            <Column
              css={hideElementCss(MediaQueries.lgAndUp)}
              cols={{ sm: 6, md: 8 }}
            >
              {fields.items.map((item, index) => {
                if (item.fields.label.value !== selectedRegusLabel) return null;
                return imagesForSmallAndMedium[`image${index + 1}`](
                  item.fields.content.value.imageRight,
                  item.fields.content.value.imageLeft,
                );
              })}
            </Column>
          </Row>
        </Container>
      </Spacer>
    );
  }
}

RegusTabs.propTypes = {
  BackgroundColor: PropTypes.shape({
    fields: PropTypes.shape({
      Value: PropTypes.shape({
        value: PropTypes.string,
      }),
    }),
  }),
  Headline: PropTypes.shape({
    value: PropTypes.string,
  }),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      tabName: PropTypes.shape({
        value: PropTypes.string.isRequired,
      }),
      label: PropTypes.shape({
        value: PropTypes.string.isRequired,
      }),
      content: PropTypes.shape({
        value: PropTypes.object.isRequired,
      }),
    }),
  ),
};

RegusTabs.displayName = 'RegusTabs';

export default RegusTabs;
